/**
 * unilar Account Modal
 * Handles the account login/actions modal functionality
 */

const ACCOUNT_MODAL_STYLESHEET_ATTRIBUTE = 'data-account-modal-stylesheet';
const ACCOUNT_MODAL_STYLESHEET_TIMEOUT = 10000;
let accountModalStylesPromise;

function loadAccountModalStyles() {
  const stylesheetUrl = document.querySelector('script[data-account-modal-stylesheet-url]')?.dataset
    .accountModalStylesheetUrl;

  if (!stylesheetUrl) {
    return Promise.reject(new Error('Account modal stylesheet URL is unavailable'));
  }

  const normalizedStylesheetUrl = new URL(stylesheetUrl, document.baseURI).href;
  const existingStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
    (link) => link.hasAttribute(ACCOUNT_MODAL_STYLESHEET_ATTRIBUTE) || link.href === normalizedStylesheetUrl
  );

  if (accountModalStylesPromise) {
    return accountModalStylesPromise;
  }

  if (existingStylesheet?.sheet) {
    return Promise.resolve(existingStylesheet);
  }

  accountModalStylesPromise = new Promise((resolve, reject) => {
    const stylesheet = existingStylesheet || document.createElement('link');
    let timeoutId;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      stylesheet.removeEventListener('load', handleLoad);
      stylesheet.removeEventListener('error', handleError);
    };

    const handleLoad = () => {
      cleanup();
      resolve(stylesheet);
    };

    const handleError = () => {
      cleanup();
      reject(new Error('Account modal stylesheet failed to load'));
    };

    stylesheet.addEventListener('load', handleLoad, { once: true });
    stylesheet.addEventListener('error', handleError, { once: true });

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Account modal stylesheet load timed out'));
    }, ACCOUNT_MODAL_STYLESHEET_TIMEOUT);

    if (!existingStylesheet) {
      stylesheet.rel = 'stylesheet';
      stylesheet.href = normalizedStylesheetUrl;
      stylesheet.setAttribute(ACCOUNT_MODAL_STYLESHEET_ATTRIBUTE, '');
      document.head.appendChild(stylesheet);
    }
  });

  return accountModalStylesPromise;
}

class unilarAccountModal {
  constructor() {
    this.modal = document.querySelector('[data-account-modal]');
    if (!this.modal) return;

    this.overlay = this.modal.querySelector('[data-account-overlay]');
    this.closeBtn = this.modal.querySelector('[data-account-close]');
    this.content = this.modal.querySelector('.unilar-account-modal-content');
    this.triggers = document.querySelectorAll('[data-account-trigger]');
    this.desktopBreakpoint = window.matchMedia('(min-width: 810px)');

    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.init();
  }

  init() {
    const preloadStyles = () => {
      loadAccountModalStyles().catch(() => {});
    };

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', preloadStyles, { once: true, passive: true });
      trigger.addEventListener('pointerdown', preloadStyles, { once: true, passive: true });
      trigger.addEventListener('focus', preloadStyles, { once: true });

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });

    document.addEventListener('unilar:open-account-modal', () => this.open());

    if (window.Shopify?.designMode === true) {
      preloadStyles();
    }
  }

  getDesktopTrigger() {
    return document.querySelector('.unilar-header-desktop [data-account-trigger]');
  }

  positionDesktop() {
    if (!this.content || !this.desktopBreakpoint.matches) return;

    const trigger = this.getDesktopTrigger();
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 8;
    const viewportPadding = 16;
    const contentWidth = this.content.offsetWidth || 400;
    let left = rect.left;

    if (left + contentWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - contentWidth - viewportPadding;
    }

    if (left < viewportPadding) {
      left = viewportPadding;
    }

    this.content.style.setProperty('--account-modal-top', `${rect.bottom + gap}px`);
    this.content.style.setProperty('--account-modal-left', `${left}px`);
  }

  clearDesktopPosition() {
    if (!this.content) return;

    this.content.style.removeProperty('--account-modal-top');
    this.content.style.removeProperty('--account-modal-left');
  }

  bindDesktopListeners() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, true);
  }

  unbindDesktopListeners() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  handleResize() {
    if (this.modal.classList.contains('active')) {
      this.positionDesktop();
    }
  }

  handleScroll() {
    if (this.modal.classList.contains('active') && this.desktopBreakpoint.matches) {
      this.positionDesktop();
    }
  }

  async open() {
    try {
      await loadAccountModalStyles();
    } catch {
      // Preserve account access if the stylesheet cannot be loaded.
    }

    this.modal.classList.add('active');
    this.modal.classList.remove('unilar-hidden');

    if (this.desktopBreakpoint.matches) {
      this.positionDesktop();
      this.bindDesktopListeners();
    }

    if (!document.body.classList.contains('unilar-mobile-menu-open')) {
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    this.modal.classList.remove('active');
    this.modal.classList.add('unilar-hidden');
    this.unbindDesktopListeners();
    this.clearDesktopPosition();

    if (!document.body.classList.contains('unilar-mobile-menu-open')) {
      document.body.style.overflow = '';
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new unilarAccountModal();
  });
} else {
  new unilarAccountModal();
}
